import React, { useEffect } from 'react';
import { PrimitiveAtom, useAtom } from 'jotai';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import * as Const from '@/assets/Const';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

type Props = {
  // atomのsetter/getter定義
  areaClass?: string;
  label: string;
  labelClass?: string;
  labelFontClass?: string;
  areaWidth?: string;
  list: Const.ListItem[] | undefined;
  listClass?: string;
  checkboxClass?: string;
  atom: PrimitiveAtom<Const.selectItem[]>;
  control: UseControllerProps<FieldValues>['control'];
  required?: boolean;
  inputAreaWidth?: string;
  inputAreaClass?: string;
  name: string;
  bigCheck?: boolean;
  useLabel?: boolean;
  listLabelClass?: string;
  useMultipleLabel?: boolean;
  checkboxLabelH4Class?: string;
  onSingleCheckChanged?: (check: boolean) => void;
  loadingReset?: boolean;
  withNewLineOnDesktop?: boolean;
  isDisabled?: boolean;
};

export const CheckboxWithLabel = (props: Props) => {
  const [selectedOptions, setValue] = useAtom(props.atom);
  const {
    areaClass = '',
    areaWidth = ' md:grid-full grid-3col',
    label,
    list,
    listClass = '',
    control,
    required = false,
    inputAreaClass = '',
    inputAreaWidth = ' md:min-w-[170px] w-grid-small',
    name,
    bigCheck = false,
    useMultipleLabel = false,
    listLabelClass = '',
    checkboxLabelH4Class = '',
    loadingReset = false
  } = props;

  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const option = updatedList?.find((item) => item.value === Number(event.target.value));
    if (!option) {
      // オプションが見つからない場合は何もしないか、エラーハンドリングを行う
      return;
    }
    if (selectedOptions.some((item) => item.value === option.value)) {
      // 選択を解除
      setValue(selectedOptions.filter((item) => item.value !== option.value));
    } else {
      // 選択を追加
      setValue([...selectedOptions, option]);
    }
  };

  const updatedList = list?.map((item) => ({
    value: item.id,
    label: item.name,
    explanation: item.explanation ? item.explanation : ''
  }));

  const labelRequiredClass = 'checkbox-label-required ';
  const labelClass = 'checkbox-label ' + props.labelClass;

  let labelTag = <></>;
  if (required) {
    labelTag = (
      <label className={labelClass} htmlFor={name}>
        <span className="font-bold">
          <h3>
            {label}
            <span className="text-base font-normal">{label && useMultipleLabel ? '（複数選択可）' : ''}</span>
          </h3>
        </span>
        <span className={labelRequiredClass}>必須</span>
      </label>
    );
  } else {
    labelTag = (
      <label className={labelClass}>
        <span>
          <h3>
            {label}
            <span className="text-base font-normal">{label && useMultipleLabel ? '（複数選択可）' : ''}</span>
          </h3>
        </span>
      </label>
    );
  }

  let checkboxLabelClass = 'checkbox-inside-label ';
  if (bigCheck) {
    checkboxLabelClass += ' w-grid-4col border border-main hover:text-main text-base';
  } else {
    checkboxLabelClass += inputAreaWidth + ' text-sm';
  }
  const listAreaClass = listClass === '' ? 'flex justify-start items-wrap' : listClass;

  // バリデーションルールを設定
  const validationRules: { required?: string } = {};
  // 必須
  if (required) {
    validationRules.required = label + 'は、最低1つを選択してください';
  }

  useEffect(() => {
    if (loadingReset) {
      // 選択をリセットする
      selectedOptions.map((val) => {
        val.value = 0;
      });
    }
  }, [props.loadingReset]);

  return (
    <div className={areaClass + areaWidth}>
      <Controller
        name={name}
        control={control}
        defaultValue={[]}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <div className={inputAreaClass}>
            <div className={'flex justify-between items-center ' + listLabelClass}>
              {labelTag}
              {error ? <span className="checkbox-tag-error">!</span> : undefined}
            </div>
            <div className={'flex md:items-nowrap flex-wrap ' + listAreaClass}>
              {updatedList?.map((option, index) => {
                return (
                  <label key={index} htmlFor={name + option.value} className={checkboxLabelClass}>
                    <input
                      type="checkbox"
                      {...field}
                      value={String(option.value)}
                      checked={selectedOptions.some((item) => item.value === option.value)}
                      onChange={(e) => {
                        handleSelectChange(e);
                        field.onChange(e.target.value);
                        console.log(e.target);
                        // チェックボックスが一つしかない時にチェックボックスがついているかを呼び出し元に戻す
                        if (props.onSingleCheckChanged !== undefined) {
                          props.onSingleCheckChanged(!selectedOptions.some((item) => item.value === option.value));
                        }
                      }}
                      className="peer hidden"
                      id={name + option.value}
                      disabled={props.isDisabled == true}
                    />
                    <span className="checkbox-icon">
                      <CheckCircleRoundedIcon />
                    </span>
                    <div>
                      <span className={bigCheck ? 'checkbox-label-text-big' : 'checkbox-label-text'}>
                        <h4 className={checkboxLabelH4Class}>
                          {props.withNewLineOnDesktop
                            ? option.label.split('\n').map((line) => (
                                <>
                                  {line} <br className="hidden md:block" />
                                </>
                              ))
                            : option.label}
                        </h4>
                        {option.explanation ? (
                          <>
                            <div className="text-gray-500 text-sm font-normal">{option.explanation}</div>
                          </>
                        ) : (
                          ''
                        )}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
            {error && <p className="mt-[10px] text-error font-bold text-xs text-left">{error.message}</p>}
          </div>
        )}
      />
    </div>
  );
};
